import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { jsPDF } from 'jspdf';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Plus, FileText, Trash2, Download, ArrowRight, Link as LinkIcon, Send, History, Eye, CheckCircle2, XCircle, Mail, Bell, Package } from 'lucide-react';
import { PresetPicker } from './AdminPresetItemsTab';
import { format, formatDistanceToNow } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminQuotesTab = ({ users, onRefresh, permissions }) => {
  const canDelete = permissions ? permissions.canDeleteQuotes : true;
  const canConvertToInvoice = permissions ? permissions.canConvertQuotesToInvoices : true;
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [creating, setCreating] = useState(false);
  const [auditDialog, setAuditDialog] = useState({ open: false, quote: null, log: [], loading: false });
  const [newQuote, setNewQuote] = useState({
    user_id: '',
    client_name: '',
    client_email: '',
    client_address: '',
    items: [{ description: '', quantity: 1, unit_price: 0 }],
    tax_rate: 0,
    notes: '',
    valid_days: 30
  });

  useEffect(() => {
    fetchQuotes();
  }, []);

  const fetchQuotes = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/quotes`, {
        withCredentials: true
      });
      setQuotes(response.data);
    } catch (error) {
      console.error('Error fetching quotes:', error);
      toast.error('Failed to fetch quotes');
    } finally {
      setLoading(false);
    }
  };

  const resetNewQuote = () => {
    setNewQuote({
      user_id: '',
      client_name: '',
      client_email: '',
      client_address: '',
      items: [{ description: '', quantity: 1, unit_price: 0 }],
      tax_rate: 0,
      notes: '',
      valid_days: 30
    });
  };

  const handleUserSelect = (userId) => {
    const user = users?.find(u => u.user_id === userId);
    setNewQuote({
      ...newQuote,
      user_id: userId,
      client_name: user?.name || '',
      client_email: user?.email || ''
    });
  };

  const addItem = () => {
    setNewQuote({
      ...newQuote,
      items: [...newQuote.items, { description: '', quantity: 1, unit_price: 0 }]
    });
  };

  const [presetOpen, setPresetOpen] = useState(false);
  const addPresetItem = (preset) => {
    const newItem = {
      description: preset.name + (preset.sku ? ` [${preset.sku}]` : ''),
      quantity: preset.default_quantity || 1,
      unit_price: preset.unit_price || 0,
    };
    // Replace the last empty row, or append if all rows have data.
    setNewQuote((prev) => {
      const items = [...prev.items];
      const lastEmpty = items.findIndex((it) => !it.description && !it.unit_price);
      if (lastEmpty >= 0) items[lastEmpty] = newItem; else items.push(newItem);
      return { ...prev, items };
    });
  };

  const removeItem = (index) => {
    setNewQuote({
      ...newQuote,
      items: newQuote.items.filter((_, i) => i !== index)
    });
  };

  const updateItem = (index, field, value) => {
    const updatedItems = [...newQuote.items];
    updatedItems[index][field] = field === 'quantity' || field === 'unit_price' ? parseFloat(value) || 0 : value;
    setNewQuote({ ...newQuote, items: updatedItems });
  };

  const calculateTotal = () => {
    const subtotal = newQuote.items.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0);
    const tax = subtotal * (newQuote.tax_rate / 100);
    return { subtotal, tax, total: subtotal + tax };
  };

  const handleCreateQuote = async (e) => {
    e.preventDefault();
    
    if (!newQuote.user_id) {
      toast.error('Please select a client');
      return;
    }
    
    if (newQuote.items.some(item => !item.description || item.unit_price <= 0)) {
      toast.error('Please fill in all item details');
      return;
    }

    setCreating(true);
    try {
      await axios.post(`${BACKEND_URL}/api/admin/quotes`, newQuote, {
        withCredentials: true
      });
      
      toast.success('Quote created successfully');
      setShowCreateDialog(false);
      resetNewQuote();
      fetchQuotes();
    } catch (error) {
      console.error('Error creating quote:', error);
      toast.error('Failed to create quote');
    } finally {
      setCreating(false);
    }
  };

  const handleStatusChange = async (quoteId, status) => {
    try {
      await axios.put(`${BACKEND_URL}/api/admin/quotes/${quoteId}/status`, 
        { status },
        { withCredentials: true }
      );
      toast.success('Quote status updated');
      fetchQuotes();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleDelete = async (quoteId) => {
    if (!window.confirm('Are you sure you want to delete this quote?')) return;
    
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/quotes/${quoteId}`, {
        withCredentials: true
      });
      toast.success('Quote deleted');
      fetchQuotes();
    } catch (error) {
      toast.error('Failed to delete quote');
    }
  };

  const handleConvertToInvoice = async (quoteId) => {
    if (!window.confirm('Convert this quote to an invoice?')) return;
    try {
      await axios.post(`${BACKEND_URL}/api/admin/quotes/${quoteId}/convert-to-invoice`, {}, {
        withCredentials: true
      });
      toast.success('Quote converted to invoice');
      fetchQuotes();
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to convert quote');
    }
  };

  const handleSendToClient = async (quote) => {
    if (!quote.client_email) {
      toast.error('Quote has no client email');
      return;
    }
    if (!window.confirm(`Send this quote to ${quote.client_email}?`)) return;
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/admin/quotes/${quote.quote_id}/send`,
        {},
        { withCredentials: true }
      );
      toast.success(res.data.message);
      fetchQuotes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send quote');
    }
  };

  const openAuditDialog = async (quote) => {
    setAuditDialog({ open: true, quote, log: [], loading: true });
    try {
      const res = await axios.get(
        `${BACKEND_URL}/api/admin/quotes/${quote.quote_id}/audit`,
        { withCredentials: true }
      );
      setAuditDialog({
        open: true,
        quote,
        log: res.data.audit_log || [],
        remindersSent: res.data.reminders_sent || [],
        lastReminderSentAt: res.data.last_reminder_sent_at,
        loading: false,
      });
    } catch (error) {
      toast.error('Failed to load activity log');
      setAuditDialog({ open: false, quote: null, log: [], loading: false });
    }
  };

  const generateQuotePDF = (quote) => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    
    // Header
    doc.setFillColor(0, 255, 0);
    doc.rect(0, 0, pageWidth, 40, 'F');
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(24);
    doc.setFont('helvetica', 'bold');
    doc.text('NEXT/GEN', 20, 25);
    doc.setFontSize(10);
    doc.text('NETWORK SOLUTIONS', 20, 32);
    
    // Quote title
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(20);
    doc.text('QUOTE', pageWidth - 20, 25, { align: 'right' });
    doc.setFontSize(10);
    doc.text(quote.quote_id, pageWidth - 20, 32, { align: 'right' });
    
    // Company info
    doc.setFontSize(9);
    doc.setTextColor(100, 100, 100);
    doc.text('P.O. Box 419, Greenup, KY 41144', 20, 50);
    doc.text('740-302-5607 | support@nextgennetworksolutions.com', 20, 56);
    
    // Client info
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text('Quote For:', 20, 70);
    doc.setFont('helvetica', 'normal');
    doc.text(quote.client_name, 20, 77);
    doc.text(quote.client_email, 20, 84);
    if (quote.client_address) {
      doc.text(quote.client_address, 20, 91);
    }
    
    // Dates
    doc.setFont('helvetica', 'bold');
    doc.text('Date:', pageWidth - 60, 70);
    doc.text('Valid Until:', pageWidth - 60, 77);
    doc.setFont('helvetica', 'normal');
    doc.text(format(new Date(quote.created_at), 'MMM dd, yyyy'), pageWidth - 20, 70, { align: 'right' });
    doc.text(format(new Date(quote.valid_until), 'MMM dd, yyyy'), pageWidth - 20, 77, { align: 'right' });
    
    // Items table header
    let yPos = 110;
    doc.setFillColor(240, 240, 240);
    doc.rect(20, yPos - 6, pageWidth - 40, 10, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.text('Description', 25, yPos);
    doc.text('Qty', 120, yPos);
    doc.text('Unit Price', 140, yPos);
    doc.text('Amount', pageWidth - 25, yPos, { align: 'right' });
    
    // Items
    yPos += 12;
    doc.setFont('helvetica', 'normal');
    quote.items.forEach(item => {
      doc.text(item.description.substring(0, 40), 25, yPos);
      doc.text(item.quantity.toString(), 120, yPos);
      doc.text(`$${item.unit_price.toFixed(2)}`, 140, yPos);
      doc.text(`$${(item.quantity * item.unit_price).toFixed(2)}`, pageWidth - 25, yPos, { align: 'right' });
      yPos += 8;
    });
    
    // Totals
    yPos += 10;
    doc.setDrawColor(200, 200, 200);
    doc.line(120, yPos - 5, pageWidth - 20, yPos - 5);
    
    doc.text('Subtotal:', 140, yPos);
    doc.text(`$${quote.subtotal.toFixed(2)}`, pageWidth - 25, yPos, { align: 'right' });
    
    if (quote.tax_rate > 0) {
      yPos += 8;
      doc.text(`Tax (${quote.tax_rate}%):`, 140, yPos);
      doc.text(`$${quote.tax_amount.toFixed(2)}`, pageWidth - 25, yPos, { align: 'right' });
    }
    
    yPos += 10;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.text('Total:', 140, yPos);
    doc.setTextColor(0, 180, 0);
    doc.text(`$${quote.total.toFixed(2)}`, pageWidth - 25, yPos, { align: 'right' });
    
    // Notes
    if (quote.notes) {
      yPos += 20;
      doc.setTextColor(0, 0, 0);
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.text('Notes:', 20, yPos);
      doc.setFont('helvetica', 'normal');
      doc.text(quote.notes, 20, yPos + 7);
    }
    
    // Footer
    doc.setTextColor(100, 100, 100);
    doc.setFontSize(8);
    doc.text('Thank you for your business!', pageWidth / 2, 280, { align: 'center' });
    
    doc.save(`Quote_${quote.quote_id}.pdf`);
    toast.success('PDF downloaded');
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'accepted': return 'bg-primary text-primary-foreground';
      case 'sent': return 'bg-blue-500/20 text-blue-500';
      case 'rejected': return 'bg-destructive/20 text-destructive';
      case 'expired': return 'bg-muted text-muted-foreground';
      default: return 'bg-yellow-500/20 text-yellow-500';
    }
  };

  const totals = calculateTotal();

  if (loading) {
    return <div className="text-center py-12">Loading quotes...</div>;
  }

  return (
    <div className="space-y-4" data-testid="admin-quotes-tab">
      {/* Header */}
      <div className="flex justify-end">
        <Dialog open={showCreateDialog} onOpenChange={(open) => {
          setShowCreateDialog(open);
          if (!open) resetNewQuote();
        }}>
          <DialogTrigger asChild>
            <Button className="font-bold uppercase">
              <Plus className="w-4 h-4 mr-2" />Create Quote
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-card border-border max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold uppercase">Create Quote</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateQuote} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label>Client *</Label>
                  <Select value={newQuote.user_id} onValueChange={handleUserSelect}>
                    <SelectTrigger className="bg-input">
                      <SelectValue placeholder="Select client" />
                    </SelectTrigger>
                    <SelectContent>
                      {users?.filter(u => u.role === 'client').map(user => (
                        <SelectItem key={user.user_id} value={user.user_id}>
                          {user.name} ({user.email})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Client Name</Label>
                  <Input value={newQuote.client_name} onChange={(e) => setNewQuote({...newQuote, client_name: e.target.value})} className="bg-input" />
                </div>
                <div>
                  <Label>Client Email</Label>
                  <Input value={newQuote.client_email} onChange={(e) => setNewQuote({...newQuote, client_email: e.target.value})} className="bg-input" />
                </div>
                <div className="col-span-2">
                  <Label>Client Address</Label>
                  <Input value={newQuote.client_address} onChange={(e) => setNewQuote({...newQuote, client_address: e.target.value})} placeholder="Optional" className="bg-input" />
                </div>
              </div>

              {/* Items */}
              <div>
                <Label className="mb-2 block">Line Items</Label>
                {newQuote.items.map((item, index) => (
                  <div key={index} className="flex gap-2 mb-2">
                    <Input
                      placeholder="Description"
                      value={item.description}
                      onChange={(e) => updateItem(index, 'description', e.target.value)}
                      className="flex-1 bg-input"
                    />
                    <Input
                      type="number"
                      placeholder="Qty"
                      value={item.quantity}
                      onChange={(e) => updateItem(index, 'quantity', e.target.value)}
                      className="w-20 bg-input"
                    />
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="Price"
                      value={item.unit_price}
                      onChange={(e) => updateItem(index, 'unit_price', e.target.value)}
                      className="w-28 bg-input"
                    />
                    {newQuote.items.length > 1 && (
                      <Button type="button" variant="outline" size="sm" onClick={() => removeItem(index)}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                ))}
                <Button type="button" variant="outline" size="sm" onClick={addItem}>
                  <Plus className="w-4 h-4 mr-1" />Add Item
                </Button>
                <Button type="button" variant="outline" size="sm" onClick={() => setPresetOpen(true)} data-testid="quote-add-preset-btn" className="ml-2">
                  <Package className="w-4 h-4 mr-1" />From Preset
                </Button>
                <PresetPicker open={presetOpen} onClose={() => setPresetOpen(false)} onPick={addPresetItem} />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Tax Rate (%)</Label>
                  <Input type="number" step="0.1" value={newQuote.tax_rate} onChange={(e) => setNewQuote({...newQuote, tax_rate: parseFloat(e.target.value) || 0})} className="bg-input" />
                </div>
                <div>
                  <Label>Valid For (days)</Label>
                  <Input type="number" value={newQuote.valid_days} onChange={(e) => setNewQuote({...newQuote, valid_days: parseInt(e.target.value) || 30})} className="bg-input" />
                </div>
                <div className="flex flex-col justify-end">
                  <div className="text-right">
                    <p className="text-sm text-muted-foreground">Subtotal: ${totals.subtotal.toFixed(2)}</p>
                    <p className="text-sm text-muted-foreground">Tax: ${totals.tax.toFixed(2)}</p>
                    <p className="text-lg font-bold text-primary">Total: ${totals.total.toFixed(2)}</p>
                  </div>
                </div>
              </div>

              <div>
                <Label>Notes</Label>
                <Textarea value={newQuote.notes} onChange={(e) => setNewQuote({...newQuote, notes: e.target.value})} placeholder="Optional notes..." className="bg-input" />
              </div>

              <Button type="submit" className="w-full font-bold uppercase" disabled={creating}>
                {creating ? 'Creating...' : 'Create Quote'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Quotes List */}
      {quotes.map((quote) => (
        <Card key={quote.quote_id} className="p-6 border-border">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <h3 className="font-bold font-mono">{quote.quote_id}</h3>
                <Badge className={getStatusColor(quote.status)}>
                  {quote.status.toUpperCase()}
                </Badge>
              </div>
              <p className="text-sm text-muted-foreground">{quote.client_name} - {quote.client_email}</p>
              <p className="text-xs text-muted-foreground mt-1">
                Created: {format(new Date(quote.created_at), 'MMM dd, yyyy')} | 
                Valid until: {format(new Date(quote.valid_until), 'MMM dd, yyyy')}
              </p>
              <p className="text-xs text-muted-foreground">{quote.items.length} item(s)</p>
              <CreatedByLine quote={quote} />
            </div>
            
            <div className="flex flex-col items-end gap-2">
              <p className="text-2xl font-black text-primary">${quote.total.toFixed(2)}</p>
              <div className="flex flex-wrap gap-2">
                <Select value={quote.status} onValueChange={(value) => handleStatusChange(quote.quote_id, value)}>
                  <SelectTrigger className="w-28 bg-input">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="draft">Draft</SelectItem>
                    <SelectItem value="sent">Sent</SelectItem>
                    <SelectItem value="accepted">Accepted</SelectItem>
                    <SelectItem value="rejected">Rejected</SelectItem>
                    <SelectItem value="expired">Expired</SelectItem>
                  </SelectContent>
                </Select>
                <Button variant="outline" size="sm" onClick={() => generateQuotePDF(quote)}>
                  <Download className="w-4 h-4 mr-1" />PDF
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => openAuditDialog(quote)}
                  title="View audit log"
                  data-testid={`audit-quote-${quote.quote_id}`}
                >
                  <History className="w-4 h-4 mr-1" />Activity
                </Button>
                {quote.client_email && ['draft', 'sent'].includes(quote.status) && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleSendToClient(quote)}
                    title={`Send the public quote link to ${quote.client_email}`}
                    data-testid={`send-quote-${quote.quote_id}`}
                  >
                    <Send className="w-4 h-4 mr-1" />
                    {quote.status === 'sent' ? 'Resend' : 'Send to Client'}
                  </Button>
                )}
                {quote.public_token && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const url = `${window.location.origin}/quote/${quote.public_token}`;
                      navigator.clipboard.writeText(url).then(
                        () => toast.success('Public quote link copied'),
                        () => toast.error('Could not copy link')
                      );
                    }}
                    title="Copy the client-facing link for this quote"
                    data-testid={`copy-public-link-${quote.quote_id}`}
                  >
                    <LinkIcon className="w-4 h-4 mr-1" />Copy Link
                  </Button>
                )}
                {quote.status !== 'accepted' && canConvertToInvoice && (
                  <Button variant="outline" size="sm" onClick={() => handleConvertToInvoice(quote.quote_id)}>
                    <ArrowRight className="w-4 h-4 mr-1" />Invoice
                  </Button>
                )}
                {canDelete && (
                  <Button variant="outline" size="sm" onClick={() => handleDelete(quote.quote_id)}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                )}
              </div>
            </div>
          </div>
        </Card>
      ))}

      {quotes.length === 0 && (
        <Card className="p-12 text-center border-border">
          <FileText className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
          <p className="text-muted-foreground">No quotes found. Create your first quote.</p>
        </Card>
      )}

      {/* Audit Log Dialog */}
      <Dialog
        open={auditDialog.open}
        onOpenChange={(open) => !open && setAuditDialog({ open: false, quote: null, log: [], loading: false })}
      >
        <DialogContent className="bg-card border-border max-w-lg" data-testid="audit-log-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold uppercase flex items-center gap-2">
              <History className="w-5 h-5 text-primary" />
              Activity — {auditDialog.quote?.quote_id}
            </DialogTitle>
          </DialogHeader>
          {auditDialog.loading ? (
            <div className="text-center py-8 text-muted-foreground text-sm">Loading…</div>
          ) : auditDialog.log.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground text-sm">
              No activity yet. Once you send the quote, view tracking and client actions will appear here.
            </div>
          ) : (
            <div className="relative max-h-96 overflow-y-auto pr-1 pl-2 py-2">
              {/* Vertical connector line — anchored to the centre of each row's icon (left:18px). */}
              <div className="absolute left-[18px] top-3 bottom-3 w-px bg-border" aria-hidden />
              <div className="space-y-3">
                {auditDialog.log.slice().reverse().map((entry, i) => (
                  <AuditRow key={i} entry={entry} />
                ))}
              </div>
            </div>
          )}
          {auditDialog.remindersSent?.length > 0 && (
            <div className="mt-3 pt-3 border-t border-border text-xs text-muted-foreground">
              Reminders sent at milestones: {auditDialog.remindersSent.join(', ')} day(s)
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

const EVENT_META = {
  created:               { label: 'Quote Created',        Icon: FileText,    accent: 'text-foreground' },
  sent:                  { label: 'Sent to Client',       Icon: Send,        accent: 'text-blue-500' },
  viewed:                { label: 'Client Viewed Quote',  Icon: Eye,         accent: 'text-amber-500' },
  accepted:              { label: 'Client Accepted',      Icon: CheckCircle2,accent: 'text-primary' },
  rejected:              { label: 'Client Rejected',      Icon: XCircle,     accent: 'text-red-500' },
  converted_to_invoice:  { label: 'Converted to Invoice', Icon: ArrowRight,  accent: 'text-primary' },
  reminder_sent:         { label: 'Reminder Sent',        Icon: Bell,        accent: 'text-amber-500' },
};

/**
 * Subtle "Created by" attribution line — pulls from the audit_log first 'created'
 * event so we get the actual staff member who authored the quote.
 */
const CreatedByLine = ({ quote }) => {
  const created = (quote.audit_log || []).find((e) => e.event === 'created');
  if (!created || !created.actor) return null;
  return (
    <p className="text-xs text-muted-foreground/70 mt-1 italic">
      Created by <span className="font-mono">{created.actor}</span>
    </p>
  );
};

const AuditRow = ({ entry }) => {
  const meta = EVENT_META[entry.event] || { label: entry.event, Icon: Mail, accent: 'text-muted-foreground' };
  const { Icon, label, accent } = meta;
  let when = '';
  try {
    when = formatDistanceToNow(new Date(entry.at), { addSuffix: true });
  } catch (e) { /* swallow — show raw */ when = entry.at; }
  const detail =
    entry.event === 'reminder_sent' && entry.meta?.milestone_days
      ? `${entry.meta.milestone_days}-day milestone`
      : entry.event === 'converted_to_invoice' && entry.meta?.invoice_id
      ? `Invoice ${entry.meta.invoice_id}`
      : entry.event === 'sent' && entry.meta?.resend
      ? 'Resent'
      : null;

  return (
    <div className="relative flex items-start gap-3 pl-1">
      <div
        className={`shrink-0 w-9 h-9 rounded-full bg-card border border-border/60 flex items-center justify-center z-10 ${accent}`}
        aria-hidden
      >
        <Icon className="w-4 h-4" />
      </div>
      <div className="flex-1 min-w-0 pt-1.5">
        <div className="flex items-center justify-between gap-2">
          <p className={`text-sm font-medium ${accent}`}>{label}</p>
          <p className="text-[11px] text-muted-foreground whitespace-nowrap">{when}</p>
        </div>
        <p className="text-xs text-muted-foreground mt-0.5">
          {entry.actor || 'system'}{detail ? ` · ${detail}` : ''}
        </p>
      </div>
    </div>
  );
};

export default AdminQuotesTab;
